import React, { useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  LinearProgress,
} from '@mui/material';
import Theme from 'theme';
import { useMutation } from 'react-query';
import { ToastProps, ToastType } from 'components/Common/Toast';
import { verifyFreeTrialOtp, sendFreeTrialOtp } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { OrganizationInfoInitialValues } from 'pages/FreeTrialActivation/components/OrganizationInfo';
import { useFreeTrialOnboarding } from 'pages/FreeTrialActivation/components/FreeTrialOnboardingContext';
import { useLocation } from 'react-router-dom';

interface VerifyEmailProps {
  onNext?: (token?: string, onboardingStatus?: string, orgData?:
    OrganizationInfoInitialValues) => void;
  onSilentLogin?: (accessToken: string) => void;
  emailMasked: string;
  step?: number;
  totalSteps?: number;
  providerId?: string;
  setToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setToastProps: (props: ToastProps | null) => void;
}

export default function VerifyEmail(
  { onNext, onSilentLogin, emailMasked, step, totalSteps = 6,
    providerId, setToken, setToastProps }: VerifyEmailProps,
) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search || '');
  const trialDays = searchParams.get('freetrialdays') || '';
  const trialCode = searchParams.get('freetrialcode') || '';
  const referralCode = searchParams.get('freetrialreferralcode') || '';
  const eventProps = useMemo(() => ({
    trial_days: trialDays,
    trial_code: trialCode,
    referral_code: referralCode,
  }), [trialDays, trialCode, referralCode]);
  const { verifyEmail, setVerifyEmail } = useFreeTrialOnboarding();
  const { code, resendTimer } = verifyEmail;

  useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_DIALOG_VIEWED, eventProps);
  }, [eventProps]);

  const mutation = useMutation({
    mutationFn: (payload: { providerId: string; code: string }) => verifyFreeTrialOtp(http
      .post, payload),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_ERROR, eventProps);
      logSentryError(`Error verifying email - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Verification failed. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: (data) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_SUCCESS, eventProps);
      if (data?.accessToken) setToken(data.accessToken);
      if (data?.onboardingStatus === 'PASSWORD_SET') {
        if (onNext) onNext(data?.accessToken, data?.onboardingStatus, data);
        return;
      }
      if ((data?.onboardingStatus === 'PAYMENT_COMPLETED' || data?.onboardingStatus === 'ONBOARDING_COMPLETED') && data?.accessToken) {
        if (onSilentLogin) onSilentLogin(data.accessToken);
        return;
      }
      if (onNext) onNext(data?.accessToken, data?.onboardingStatus);
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: (id: string) => sendFreeTrialOtp(http.post, id),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_RESEND_CODE_ERROR, eventProps);
      logSentryError(`Error resending verification code - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Failed to resend code. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_RESEND_CODE_SUCCESS, eventProps);
      setToastProps({
        message: 'Verification code resent.',
        open: true,
        type: ToastType.success,
      });
    },
  });

  const handleVerify = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_VERIFY_BUTTON_CLICKED, eventProps);
    if (!providerId || !code) return;
    mutation.mutate({ providerId, code: code.trim() });
  };


  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setVerifyEmail((prev) => ({ ...prev,
        resendTimer: prev.resendTimer - 1 })), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, setVerifyEmail]);

  const handleResend = (e: React.MouseEvent) => {
    e.preventDefault();
    if (resendTimer > 0) return;
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_VERIFY_EMAIL_RESEND_CODE_CLICKED, eventProps);
    if (providerId) {
      resendOtpMutation.mutate(providerId, {
        onSuccess: () => setVerifyEmail((prev) => ({ ...prev, resendTimer: 60 })),
      });
    }
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0, pl: 3, pt: 2 }}>
        Verify your email
      </DialogTitle>
      <LinearProgress
        variant='determinate'
        value={(step as number / totalSteps as number) * 100}
        color='success'
        sx={{
          width: '100%',
          borderRadius: '24px 24px 0 0',
          height: 4,
          my: 1,
        }}
      />
      <DialogContent sx={{ pt: 1 }}>
        <Typography fontSize={24} mb={2}>
          We’ve sent you a one-time code
        </Typography>
        <Typography variant='body1' mb={3}>
          To continue,
          <b>
            {' '}
            enter the code
          </b>
          {' '}
          we just sent to
          {' '}
          {emailMasked}
        </Typography>
        <Box component='form' noValidate autoComplete='off' onSubmit={handleVerify}>
          <TextField
            label='Enter code'
            variant='outlined'
            sx={{ mb: 3, width: '65%' }}
            value={code}
            onChange={(e) => setVerifyEmail((prev) => ({ ...prev, code: e.target.value }))}
            className='fs-mask'
          />
          <Button
            variant='contained'
            sx={{
              display: 'flex',
              px: 6,
              py: 1.5,
              background: Theme.custom.colors.lightPrimaryMain,
              color: Theme.custom.colors.white,
              fontSize: 15,
              borderRadius: 8,
              boxShadow: 'none',
              mb: 2,
            }}
            disabled={!code || mutation.isLoading}
            onClick={handleVerify}
            type='submit'
          >
            VERIFY YOUR EMAIL
          </Button>
        </Box>
        <Typography fontSize={16} mt={1}>
          Didn’t receive a code?
          <Link
            href='#'
            underline='none'
            sx={{
              color: resendTimer > 0 ? Theme.custom.colors.lightTextSecondary
                : Theme.custom.colors.lightPrimaryMain,
              fontSize: '16px',
              fontWeight: 400,
              cursor: resendOtpMutation.isLoading || resendTimer > 0 ? 'not-allowed' : 'pointer',
              ml: 3,
              pointerEvents: resendOtpMutation.isLoading || resendTimer > 0 ? 'none' : 'auto',
            }}
            onClick={resendTimer > 0 ? undefined : handleResend}
          >
            {resendTimer > 0 ? `Resend code (${resendTimer}s)` : 'Resend code'}
          </Link>
        </Typography>
      </DialogContent>
    </>
  );
}
